import { Collapse, IconButton, makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { AlertCircle, Eye, EyeOff } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { probabilities } from '../../../types/common';
import { consequenceIndex, likelihoodIndex, overallRiskMatrix } from '../../../types/plan';
import { StatusIcon } from '../../global';

const useRiskMatrixClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        borderRadius: 5,
        background: theme.colors.neutral[4],
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    risk: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h6,
            marginBottom: 0,
            textTransform: 'uppercase',
            '&.moderate': { color: '#FEDF9A' },
            '&.moderate-high': { color: '#FF8A65' },
            '&.high': { color: '#BE4242' },
        },
    },
    matrix: {
        display: 'grid',
        gridTemplateRows: 'repeat(5, 45px) 30px',
        gridTemplateColumns: '30px repeat(5, 45px)',
        gap: 5,
        marginTop: 10,
    },
    collapse: {
        alignSelf: 'center',
        justifySelf: 'center',
        padding: 8,
        color: theme.colors.neutral[1],
    },
    axis: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[2],
        ...theme.typography.headings.h6,
        marginBottom: 0,
        textTransform: 'uppercase',
    },
    tick: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[3],
        ...theme.typography.content.caption.paragraph,
        textTransform: 'uppercase',
    },
    cell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[1],
        borderRadius: 2,
        '&.selected': {
            border: '1px solid currentColor',
        },
        '&.low': { background: theme.colors.white },
        '&.moderate': { background: '#FEDF9A' },
        '&.moderate-high': { background: '#FF8A65' },
        '&.high': { background: '#BE4242' },
    },
});

/**
 * Render a risk matrix for the given `likelihood` and `consequence` levels.
 *
 * @param props.likelihood risk likelihood
 * @param props.consequence risk consequence
 * @param props.collapse if the container is collapsible
 * @param props.collapsed if the matrix starts already collapsed (`props.collapse` also needs to be true)
 */
export const RiskMatrix = (props: {
    likelihood?: 0 | 1 | 2 | 3 | 4 | typeof probabilities[number];
    consequence?: 0 | 1 | 2 | 3 | 4 | typeof probabilities[number];
    collapse?: boolean;
    collapsed?: boolean;
}) => {
    const classes = useRiskMatrixClasses();
    const { t } = usePrefixTranslation(['asset', 'common'], { asset: 'common.RiskMatrix' });
    const [display, setDisplay] = React.useState(props.collapse && !props.collapsed);
    const [row, col] = [
        typeof props.likelihood === 'string' ? likelihoodIndex[props.likelihood] : props.likelihood,
        typeof props.consequence === 'string' ? consequenceIndex[props.consequence] : props.consequence,
    ];
    const risk = row != undefined && col != undefined ? overallRiskMatrix[row][col] : 'not-defined';

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <StatusIcon style={{ gridArea: '1/1/auto/auto' }} type='warning' icon={<AlertCircle />} />
                <div className={classes.risk}>
                    <span>{t('risk')}</span>
                    <span className={risk}>{t(`common:probability.${risk}`)}</span>
                </div>
                <IconButton className={classes.collapse} onClick={() => setDisplay(!display)}>
                    {display ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
                </IconButton>
            </div>
            <Collapse classes={{ wrapperInner: classes.matrix }} in={display}>
                <div className={classes.tick} style={{ gridArea: '5/1/auto/auto' }}>
                    {t('low')}
                </div>
                <div className={classes.tick} style={{ gridArea: '6/2/auto/auto' }}>
                    {t('low')}
                </div>
                <div className={classes.tick} style={{ gridArea: '1/1/auto/auto' }}>
                    {t('high')}
                </div>
                <div className={classes.tick} style={{ gridArea: '6/6/auto/auto' }}>
                    {t('high')}
                </div>
                <div
                    className={classes.axis}
                    style={{ gridArea: '2/1/5/auto', writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
                >
                    {t('likelihood')}
                </div>
                <div className={classes.axis} style={{ gridArea: '6/3/auto/6' }}>
                    {t('consequence')}
                </div>
                {[...Array(25)].map((_, k) => {
                    const [i, j] = [Math.floor(k / 5), k % 5];
                    const cls = { [overallRiskMatrix[i][j]]: true, selected: i === row && j === col };
                    return (
                        <div
                            key={k}
                            className={clsx(classes.cell, cls)}
                            style={{ gridArea: `${1 + i}/${2 + j}/auto/auto` }}
                        >
                            {i === row && j === col && <Close />}
                        </div>
                    );
                })}
            </Collapse>
        </div>
    );
};
